import { parse, differenceInMonths, startOfMonth } from 'date-fns';
import * as yup from 'yup';

export const formatOccupancyDate = (date: string | Date): string => {
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  }

  const dateStr = String(date);

  if (/^\d{2}\/\d{4}$/.test(dateStr)) {
    const [month, year] = dateStr.split('/');
    return `${year}-${month}-01`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split('-');
    return `${year}-${month}-01`;
  }

  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split('-');
    return `${year}-${month}-01`;
  }

  throw new Error('Invalid date format. Expected formats: MM/YYYY, YYYY-MM-DD, or YYYY-MM');
};

export const formatOccupancyDateToUI = (dateStr: string) => {
  if (!dateStr) return '';
  const [year, month] = dateStr.split('-');
  return `${month}/${year}`;
};

export function formatPhoneNumber(number: string) {
  const cleanNumber = number.replace(/[()\s-]/g, '');
  return cleanNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
}

export const formatBirthDate = (date: string) => {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
    throw new Error('Invalid date format. Expected format: MM/DD/YYYY');
  }
  const [month, day, year] = date.split('/');
  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

export const formatInput = (input: string, propertyName: string): string => {
  input = input.replace(/\D/g, '');

  if (propertyName === 'propertyOccupancyDate') {
    if (input.length > 2) {
      return `${input.slice(0, 2)}/${input.slice(2, 6)}`;
    }
    return input;
  } else {
    if (input.length > 2 && input.length <= 4) {
      return `${input.slice(0, 2)}/${input.slice(2, 4)}`;
    } else if (input.length > 4) {
      return `${input.slice(0, 2)}/${input.slice(2, 4)}/${input.slice(4, 8)}`;
    }
    return input;
  }
};

export const monthsBetweenDateAndToday = (dateStr: string, propertyName: string, endDate?: string): number => {
  let inputDate;
  let inputEndDate;

  try {
    const dateFormatRegex = propertyName === 'propertyOccupancyDate' ? /^(0[1-9]|1[0-2])\/\d{4}$/ : /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

    if (!dateFormatRegex.test(dateStr)) {
      return 0;
    }

    if (propertyName === 'propertyOccupancyDate') {
      inputDate = parse(dateStr, 'MM/yyyy', new Date());
    } else {
      inputDate = parse(dateStr, 'MM/dd/yyyy', new Date());
      if (isNaN(inputDate.getTime())) {
        inputDate = parse(dateStr, 'MM/DD/yyyy', new Date());
      }
    }

    if (endDate) {
      if (typeof endDate === 'string') {
        if (!/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(endDate)) {
          return 0;
        }

        inputEndDate = parse(endDate, 'MM/dd/yyyy', new Date());
        if (isNaN(inputEndDate.getTime())) {
          inputEndDate = parse(endDate, 'MM/DD/yyyy', new Date());
        }
      } else {
        inputEndDate = new Date(endDate);
      }
    } else {
      inputEndDate = new Date();
    }

    const currentYear = new Date().getFullYear();
    const startYear = inputDate.getFullYear();
    const endYear = inputEndDate.getFullYear();

    if (isNaN(inputDate.getTime()) || isNaN(inputEndDate.getTime()) || startYear < 1900 || startYear > currentYear + 10 || endYear < 1900 || endYear > currentYear + 10) {
      return 0;
    }

    const normalizedStart = startOfMonth(inputDate);
    const normalizedEnd = startOfMonth(inputEndDate);
    const months = differenceInMonths(normalizedEnd, normalizedStart);

    return Math.max(0, months);
  } catch (error) {
    return 0;
  }
};

export const createValidationSchema = (propertyName: string, isRequired: boolean) => {
  let schema = yup.string();

  if (propertyName === 'propertyOccupancyDate') {
    schema = schema.matches(/^(0[1-9]|1[0-2])\/\d{4}$/, 'Date must be in MM/YYYY format');
  }

  if (isRequired) {
    schema = schema.required('Date is required');
  }

  return yup.object().shape({
    [propertyName]: schema,
  });
};

export const getCurrentDate = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const year = today.getFullYear();

  return `${month}/${day}/${year}`;
};
